import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import * as PR from "../../prime-modules/index";
import AdminFooter from "../layout/admin-footer";
import AdminHeader from "../layout/admin-header";
import { useSelector, useDispatch } from 'react-redux';
import { getSubscriptionPlans, uploadSubscriptionPlanContractDocuments, downloadSubscriptionPlanContractDocument, deleteSubscriptionPlanContractDocuments } from "../../services/api.jsx";
import { formatPriceCurrency } from "../../utils";
import { checkDataSize, filterCountryName } from "../../utils/reuse.js";
import "./SubscriptionPlans.scss";

const emptyContractFiles = {
    contractInformation: null,
    contractSummary: null,
};

const SubscriptionPlans = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);
    const adminData = useSelector(state => state.adminAuth.adminSessionData);
    const headers = useMemo(() => {
        return { sessionid: adminData.sessionId };
    }, [adminData.sessionId]);

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [documentDialogVisible, setDocumentDialogVisible] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [documentFiles, setDocumentFiles] = useState(emptyContractFiles);
    const [uploadingDocuments, setUploadingDocuments] = useState(false);
    const [downloadingDocument, setDownloadingDocument] = useState({
        contractInformation: false,
        contractSummary: false,
    });
    const [deletingPlan, setDeletingPlan] = useState(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [planToDelete, setPlanToDelete] = useState(null);

    // Fetch subscription plans and merge contract-documents list
    const fetchSubscriptionPlans = useCallback(() => {
        setLoading(true);
        getSubscriptionPlans(headers, dispatch, (response) => {
            setLoading(false);
            if (response.result === "SUCCESS") {
                setPlans(response.data || []);
            }
        });
    }, [dispatch, headers]);


    useEffect(() => {
        fetchSubscriptionPlans();
    }, [fetchSubscriptionPlans]);

    const handleOpenDocumentDialog = (plan) => {
        setSelectedPlan(plan);
        setDocumentFiles(emptyContractFiles);
        setDocumentDialogVisible(true);
    };

    const contractInfoInputRef = React.useRef(null);
    const contractSummaryInputRef = React.useRef(null);

    const handleDocumentFileChange = (event, field) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        setDocumentFiles((prev) => ({ ...prev, [field]: file }));
        try { event.target.value = ''; } catch (e) {}
    };

    const openFilePicker = (field) => {
        if (field === 'contractInformation') {
            contractInfoInputRef.current?.click();
        } else {
            contractSummaryInputRef.current?.click();
        }
    };

    const handleUploadDocuments = () => {
        if (!selectedPlan || !selectedPlan.name) {
            return;
        }

        const formData = new FormData();
        if (documentFiles.contractInformation) {
            formData.append('contractInformation', documentFiles.contractInformation);
        }
        if (documentFiles.contractSummary) {
            formData.append('contractSummary', documentFiles.contractSummary);
        }

        setUploadingDocuments(true);
        uploadSubscriptionPlanContractDocuments(selectedPlan.name, formData, headers, dispatch, (response) => {
            setUploadingDocuments(false);
            if (response.result === 'SUCCESS') {
                setDocumentDialogVisible(false);
                setDocumentFiles(emptyContractFiles);
                fetchSubscriptionPlans();
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Contract documents uploaded successfully.',
                });
            } else {
                const error = response.error;
                const detail = error?.errorMsg || error?.summary || 'Unable to upload contract documents.';
                toast.current?.show({ severity: 'error', summary: 'Error', detail });
            }
        });
    };

    const triggerDownload = (plan, documentType) => {
        if (!plan || !plan.name) {
            return;
        }

        setDownloadingDocument((prev) => ({ ...prev, [documentType]: true }));
        // map internal documentType keys to API docType enum
        const docTypeMap = {
            contractInformation: 'contract-information',
            contractSummary: 'contract-summary',
        };
        const apiDocType = docTypeMap[documentType] || documentType;
        downloadSubscriptionPlanContractDocument(plan.name, apiDocType, headers, dispatch, (response) => {
            setDownloadingDocument((prev) => ({ ...prev, [documentType]: false }));
            if (response.result !== 'SUCCESS') {
                const error = response.error;
                const detail = error?.errorMsg || error?.summary || 'Unable to download contract document.';
                toast.current?.show({ severity: 'error', summary: 'Error', detail });
                return;
            }

            const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const disposition = response.headers?.['content-disposition'] || '';
            const fileName = disposition.split('filename=')[1]?.replace(/['"]/g, '') || `${plan.name}-${documentType}.pdf`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        });
    };

    const handleDeleteDocuments = (plan) => {
        if (!plan || !plan.name) return;
        setPlanToDelete(plan);
        setDeleteDialogVisible(true);
    };

    const confirmDeleteDocuments = () => {
        if (!planToDelete || !planToDelete.name) return;
        setDeletingPlan(planToDelete.name);
        setDeleteDialogVisible(false);
        deleteSubscriptionPlanContractDocuments(planToDelete.name, headers, dispatch, (response) => {
            setDeletingPlan(null);
            setPlanToDelete(null);
            if (response.result === 'SUCCESS') {
                fetchSubscriptionPlans();
                toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Contract documents deleted.' });
            } else {
                const error = response.error;
                const detail = error?.errorMsg || error?.summary || 'Unable to delete contract documents.';
                toast.current?.show({ severity: 'error', summary: 'Error', detail });
            }
        });
    };

    // Columns formatters
    const setDataAmount = (rowData) => {
        return rowData.dataAmountForDisplay ? rowData.dataAmountForDisplay : checkDataSize(rowData.dataAmount);
    };

    const setDataDuration = (rowData) => {
        if (!rowData.duration) return "-";
        const unit = rowData.duration_unit ? rowData.duration_unit.toLowerCase() : "day";
        const plural = rowData.duration > 1 ? "s" : "";
        return `${rowData.duration} ${unit}${plural}`;
    };

    const priceBodyTemplate = (rowData) => {
        return (
            <span>{formatPriceCurrency(rowData?.priceCurrency, rowData?.price)}</span>
        );
    };

    const providerCodeBodyTemplate = (rowData) => {
        const providerMap = {
            1: 'eSIM-Go',
            2: 'Airalo',
            3: 'Cobira',
            4: 'eSIM-Go'
        };
        return (
            <span>{providerMap[rowData.providerCode] || rowData.providerCode || "-"}</span>
        );
    };

    const displayCountry = (rowData) => {
        const countries = rowData.countries || [];
        if (countries.length === 0) {
            return rowData.iso ? rowData.iso : "-";
        }
        return countries
            .map((c) => filterCountryName(c.country?.name))
            .filter(Boolean)
            .join(", ") || "-";
    };

    const contractDocumentsTemplate = (rowData) => {
        const hasContractDocuments = !!rowData?.hasContractDocuments;

        return (
            <div className="contract-documents-cell">
                {!hasContractDocuments && (
                    <div className="contract-documents-actions">
                        <PR.Button
                            label={'Upload Documents'}
                            icon="pi pi-upload"
                            size="small"
                            severity={'secondary'}
                            onClick={() => handleOpenDocumentDialog(rowData)}
                            className="upload-btn"
                        />
                    </div>
                )}
                {hasContractDocuments && (
                    <div className="doc-tiles">
                        <div className="doc-tile">
                            <PR.Button
                                icon="pi pi-download"
                                className={`doc-tile-button download `}
                                aria-label="Contract Information"
                                onClick={() => triggerDownload(rowData, 'contractInformation')}
                            />
                            <div className="doc-tile-label">{downloadingDocument.contractInformation ? 'Downloading...' : 'Information'}</div>
                        </div>

                        <div className="doc-tile">
                            <PR.Button
                                icon="pi pi-download"
                                className={`doc-tile-button summary`}
                                aria-label="ContractSummary"
                                onClick={() => triggerDownload(rowData, 'contractSummary')}
                            />
                            <div className="doc-tile-label">{downloadingDocument.contractSummary ? 'Downloading...' : 'Summary'}</div>
                        </div>

                        <div className="doc-tile" onClick={() => handleOpenDocumentDialog(rowData)} style={{ cursor: 'pointer' }}>
                            <PR.Button
                                icon="pi pi-upload"
                                className="doc-tile-button upload"
                                aria-label="Upload Documents"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDocumentDialog(rowData);
                                }}
                            />
                            <div className="doc-tile-label">Edit Documents</div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="main">
                <div className="layout-sidebar">
                    <AdminHeader />
                </div>
                <div className="layout-content-wrapper">
                    <section className="admin-users-section price-adjust-section subscription-plans-section">
                        <div className="grid grid-nogutter">
                            <div className="col-12">
                                <div className="heading-sec">
                                    <div className="flex align-items-center justify-content-between mobile-flex">
                                        <div className="flex align-items-center justify-content-between width-full">
                                            <h1>Subscription Plans</h1>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="users-data-table price-adjustment subscription-plans-table card mt-4">
                            <PR.DataTable
                                loading={loading}
                                value={plans}
                                className="price-adjustment-table"
                                emptyMessage={<span className="text-gray-500">No results found</span>}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 20, 50]}
                                paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                                paginatorClassName="select-bundle-pagination"
                                sortField="providerCode"
                                sortOrder={1}
                            >
                                <PR.Column field="providerCode" header="Provider Code" body={providerCodeBodyTemplate} style={{ width: "18%" }} sortable />
                                <PR.Column 
                                    body={(rowData) => (
                                        <div>
                                            <div>{rowData.description || "-"}</div>
                                            {rowData.duration && (
                                                <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>
                                                    {setDataDuration(rowData)}
                                                </div>
                                            )}
                                        </div>
                                    )}  
                                    header="Plan" 
                                    field="description" 
                                    style={{ width: "18%" }} 
                                    sortable 
                                />
                                <PR.Column 
                                    body={(rowData) => (
                                        <div>
                                            <div>{rowData.name || "-"}</div>
                                            {rowData.groups && (
                                                <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>
                                                    {rowData.groups}
                                                </div>
                                            )}
                                        </div>
                                    )}  
                                    header="Bundle Name" 
                                    field="name" 
                                    style={{ width: "20%" }} 
                                    sortable 
                                />
                                <PR.Column field="dataAmount" header="Data" style={{ width: "11%" }} body={setDataAmount} sortable />
                                <PR.Column field="price" header="Retail Price" style={{ width: "17%" }} body={priceBodyTemplate} sortable />
                               {/*  <PR.Column field="bundleType" header="Bundle Type" style={{ width: "15%" }} sortable /> */}
                                <PR.Column header="Country" body={displayCountry} style={{ width: "20%" }} />
                                <PR.Column header="Contract Documents" body={contractDocumentsTemplate} style={{ width: "30%" }} />
                            </PR.DataTable>
                        </div>
                    </section>

                    <PR.Dialog
                        visible={documentDialogVisible}
                        dismissableMask={true}
                        draggable={false}
                        resizable={false}
                        header={selectedPlan ? `${selectedPlan.name || selectedPlan.description || 'Plan'} Contract Documents` : 'Contract Documents'}
                        onHide={() => { setDocumentDialogVisible(false); setDocumentFiles(emptyContractFiles); }}
                        className="affiliate-dialog"
                        maskClassName="affiliate-dialog-mask"
                        style={{ width: '90%', maxWidth: '600px' }}
                    >
                        <div style={{ marginTop: '1rem' }} className="onboard-dialog-content">
                            <div className="flex flex-column gap-2 mb-3">
                                <p className="m-0 font-semibold text-sm">
                                    Contract Information PDF {!selectedPlan?.hasContractDocuments && <span className="text-red-500">*</span>}
                                </p>
                                <div className="flex gap-2">
                                    <PR.InputText
                                        value={documentFiles.contractInformation ? documentFiles.contractInformation.name : ''}
                                        placeholder="No file chosen"
                                        className="flex-1"
                                        readOnly
                                    />
                                    <input
                                        ref={contractInfoInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleDocumentFileChange(e, 'contractInformation')}
                                    />
                                    <PR.Button
                                        label="Choose file"
                                        onClick={() => openFilePicker('contractInformation')}
                                        className="confirm-button"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-column gap-2 mb-3">
                                <p className="m-0 font-semibold text-sm">
                                    Contract Summary PDF {!selectedPlan?.hasContractDocuments && <span className="text-red-500">*</span>}
                                </p>
                                <div className="flex gap-2">
                                    <PR.InputText
                                        value={documentFiles.contractSummary ? documentFiles.contractSummary.name : ''}
                                        placeholder="No file chosen"
                                        className="flex-1"
                                        readOnly
                                    />
                                    <input
                                        ref={contractSummaryInputRef}
                                        type="file"
                                        accept="application/pdf"
                                        style={{ display: 'none' }}
                                        onChange={(e) => handleDocumentFileChange(e, 'contractSummary')}
                                    />
                                    <PR.Button
                                        label="Choose file"
                                        onClick={() => openFilePicker('contractSummary')}
                                        className="confirm-button"
                                    />
                                </div>
                            </div>

                            <div className="dialog-footer buttons-sections flex gap-3 align-items-center justify-content-end mt-5 p-0">
                                <PR.Button
                                    label="Cancel"
                                    type="button"
                                    onClick={() => setDocumentDialogVisible(false)}
                                    className="confirm-button reset-btn min-width"
                                    disabled={uploadingDocuments}
                                />
                                <PR.Button
                                    label={uploadingDocuments ? 'Submitting...' : 'Submit'}
                                    onClick={handleUploadDocuments}
                                    className="confirm-button min-width"
                                    disabled={uploadingDocuments || (!documentFiles.contractInformation && !documentFiles.contractSummary)}
                                />
                            </div>
                        </div>
                    </PR.Dialog>

                    <PR.Dialog
                        visible={deleteDialogVisible}
                        header="Confirm Deletion"
                        className="delete-kyc-dialog"
                        draggable={false}
                        resizable={false}
                        modal
                        onHide={() => setDeleteDialogVisible(false)}
                    >
                        <div className="confirmation-content flex align-items-center">
                            <i className="pi pi-exclamation-triangle mr-3 text-red-500" style={{ fontSize: "2rem" }} />
                            {planToDelete && (
                                <span>
                                    Are you sure you want to delete contract documents for <b>{planToDelete.name || planToDelete.description || planToDelete}</b>?
                                </span>
                            )}
                        </div>
                        <div className="flex justify-content-end mt-4 buttons-sections gap-2">
                            <PR.Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text confirm-button reset-btn"
                                onClick={() => setDeleteDialogVisible(false)}
                                disabled={!!deletingPlan}
                            />
                            <PR.Button
                                label="Delete"
                                icon="pi pi-trash"
                                severity="danger"
                                className="confirm-button yes"
                                onClick={confirmDeleteDocuments}
                                loading={!!deletingPlan}
                            />
                        </div>
                    </PR.Dialog>

                    <PR.Toast ref={toast} position="top-right" />

                    <AdminFooter />
                </div>
            </div>
        </>
    );
};

export default SubscriptionPlans;
